/**
 * This month's copy of a file, read into a set of data that already exists.
 *
 * A set of data linked to a file in Google Drive or OneDrive is refreshed by reading
 * the file again, and reading it again produces columns from scratch: names derived
 * from the headings, types from a fresh vote. Using those as they are would undo two
 * things somebody relied on. A design binds to a column's `name`, and a column renamed
 * on the review screen keeps its old name underneath — so a fresh name would unbind
 * it. And a type somebody corrected ("these reference numbers are text") would be
 * voted back to what it was before they corrected it, silently, in next month's
 * invoices.
 *
 * So each column in the file is matched to the column it was last time, by the
 * heading as it appears in the file, and keeps that column's name, label, switch and
 * type. A kept type only survives while every value still reads as it: a money column
 * that has grown a "TBC" would otherwise become a blank on the page, which is worse
 * than a column that comes back as text and says so.
 */
import { blank, coerce } from "./infer.js";
import { summarise } from "./edit.js";
import { identifier, placeholderLabel } from "./names.js";
import type { Dataset, SourceColumn } from "./types.js";

export interface Refreshed {
  columns: SourceColumn[];
  rows: Array<Record<string, unknown>>;
  /** Headings in the file that were not there last time. */
  added: string[];
  /** Columns that were in use and are not in the file any more. A design using one prints a blank. */
  removed: string[];
  /** Columns whose kept type no longer fits every value, so the file's own reading was used. */
  retyped: string[];
}

export function refreshDataset(existing: SourceColumn[], fresh: Dataset): Refreshed {
  const unmatched = [...existing];
  const take = (found: SourceColumn | undefined) => {
    if (found) unmatched.splice(unmatched.indexOf(found), 1);
    return found;
  };
  // The heading first, because it is what the person sees and edits in the file; the
  // name second, which catches a heading whose spacing or punctuation changed but
  // which still means the same column.
  const pairs = fresh.columns.map((column) => ({
    column,
    was: take(unmatched.find((c) => sameHeading(c.header, column.header))) ?? take(unmatched.find((c) => c.name === column.name))
  }));

  const taken = new Set(pairs.flatMap((p) => (p.was ? [p.was.name] : [])));
  const added: string[] = [];
  const retyped: string[] = [];
  const out: Array<{ column: SourceColumn; from: string; kept: boolean }> = [];

  for (const [index, { column, was }] of pairs.entries()) {
    if (!was) {
      // A new heading gets a name nothing else has, including a name a kept column
      // still carries from a heading that has since been changed.
      const base = column.name || identifier(column.header) || `column${index + 1}`;
      let name = base;
      for (let n = 2; taken.has(name); n++) name = `${base}${n}`;
      taken.add(name);
      added.push(column.label || placeholderLabel(index));
      out.push({ column: { ...column, name }, from: column.name, kept: false });
      continue;
    }

    const sameType = was.type === column.type;
    const kept: SourceColumn = {
      ...column,
      name: was.name,
      label: was.label,
      include: was.include,
      type: was.type,
      // A percentage's format records whether the file wrote `12.5` or `0.125`, which
      // is a fact about this copy of the file rather than a choice; everything else
      // is how somebody chose to see it.
      format: sameType && column.type === "percent" ? column.format : was.format,
      currency: was.currency
    };
    if (sameType) {
      out.push({ column: kept, from: column.name, kept: true });
      continue;
    }
    const fits = fresh.rows.every((row) => blank(row[column.name]) || !blank(coerce(row[column.name], vote(kept))));
    if (fits) out.push({ column: kept, from: column.name, kept: true });
    else {
      retyped.push(was.label);
      out.push({ column: { ...column, name: was.name, label: was.label, include: was.include }, from: column.name, kept: false });
    }
  }

  const rows = fresh.rows.map((row) => {
    const next: Record<string, unknown> = {};
    for (const { column, from, kept } of out) {
      const value = row[from] ?? null;
      next[column.name] = kept && column.type !== fresh.columns.find((c) => c.name === from)!.type ? coerce(value, vote(column)) : value;
    }
    return next;
  });

  const described = summarise(
    out.map((o) => o.column),
    rows
  );
  return {
    // `summarise` is for values somebody typed against a type they chose; a file's
    // vote is still a vote, so a column the review screen would ask about still asks.
    columns: described.map((c, i) => ({ ...c, confidence: out[i]!.kept && out[i]!.column.type !== fresh.columns[i]!.type ? 1 : fresh.columns[i]!.confidence })),
    rows,
    added,
    removed: unmatched.filter((c) => c.include).map((c) => c.label),
    retyped
  };
}

function sameHeading(a: string, b: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  return norm(a) !== "" && norm(a) === norm(b);
}

function vote(column: SourceColumn) {
  return {
    type: column.type,
    format: column.format,
    currency: column.currency,
    confidence: column.confidence,
    constant: column.constant,
    blankRatio: column.blankRatio,
    samples: column.samples
  };
}
